<template>
  <div class="flex flex-col w-full mt-2 gap-3 md:flex-row md:items-center md:mt-0 md:gap-0">
    <n-popover trigger="manual" :show="isQuickSearchHintShown" :show-arrow="false" placement="bottom-start" @clickoutside="hideSearchHint">
      <template #trigger>
        <etg-input
          ref="inputRef"
          v-model:value="keyword"
          class="search-bar flex-1 w-full ring-1 ring-gray-40 text-base md:text-lg md:ring-0"
          :placeholder="$t('search.placeholder.home', { region_name: regionName })"
          enter-key-hint="search"
          @click="handleClickSearchBar"
          @submit="handleSubmit"
        >
          <template v-if="appStore.isMobileScreen" #prefix>
            <div class="p-1 mr-1">
              <svg-icon name="search" class="size-4" color="black-50" />
            </div>
          </template>
        </etg-input>
      </template>
      <template #default>
        <div v-if="!appStore.isMobileScreen" class="search-hint-wrapper">
          <search-hint :style="searchHintWrapperStyle" :keyword="debouncedKeyword" @clear="onSearchHintClear" @search-click="handleSearchHintClick" />
        </div>
      </template>
    </n-popover>
    <etg-button type="primary" class="w-full md:w-auto md:p-3.5" :aria-label="$t('search.button')" @click="handleSubmit">
      <template v-if="appStore.isMobileScreen">{{ $t('search.button') }}</template>
      <svg-icon v-else name="search" class="size-6 text-white" />
    </etg-button>
    <div v-if="appStore.isMobileScreen" class="hidden">
      <mobile-search-button
        v-model="isMobileSearchShown"
        v-model:keyword="keyword"
        class="ml-auto"
        :search-input-placeholder="$t('search.placeholder.home', { region_name: regionName })"
        @submit="handleSubmit"
        @search-click="handleSearchHintClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { NPopover } from 'naive-ui'
import type { MaybeComputedElementRef } from '@vueuse/core'

import EtgInput from '@/components/form/etg-input.vue'
import SearchHint from '@/components/search/search-hint.vue'
import MobileSearchButton from '@/components/search/mobile-search-button.vue'
import { useAppStore } from '@/store/app'

import type { ElasticsearchCompleteKeyword } from '@/types/api'
import { SearchType, type SearchClickPayload } from '@/types/search'

interface Emits {
  (event: 'search', searchItem: ElasticsearchCompleteKeyword): void
}

const emit = defineEmits<Emits>()

const appStore = useAppStore()

const { regionName } = useRegion()
const { keywordSource, fireSearchQuerySentEvent } = useGtmSearchQuerySent()

const inputRef = ref<Nullable<HTMLInputElement>>(null)
const { width: inputWidth } = useElementBounding(inputRef as MaybeComputedElementRef)
const isQuickSearchHintShown = ref(false)
const isMobileSearchShown = ref(false)
const keyword = ref('')
const debouncedKeyword = refDebounced(keyword, 300)

const searchHintWrapperStyle = computed(() => ({
  width: `${inputWidth.value}px`,
}))

function buildSearchEventOptions({ isEnter = false, type = SearchType.KEYWORD } = {}) {
  return {
    keyword: keyword.value,
    isEnter,
    type,
  }
}

function hideSearchHint() {
  isQuickSearchHintShown.value = false
}

function handleClickSearchBar() {
  isQuickSearchHintShown.value = true
  isMobileSearchShown.value = true
  if (inputRef.value) inputRef.value.focus()
}

function handleSubmit() {
  hideSearchHint()
  fireSearchQuerySentEvent(buildSearchEventOptions({ isEnter: true }))
  emit('search', { text: keyword.value, type: SearchType.KEYWORD })
}

function handleSearchHintClick({ item, keywordSource: source }: SearchClickPayload) {
  keyword.value = item.text || ''
  keywordSource.value = source
  hideSearchHint()
  fireSearchQuerySentEvent(buildSearchEventOptions({ type: item.type }))
  emit('search', item)
}

function onSearchHintClear(hasAnyHint: boolean) {
  if (!hasAnyHint) {
    hideSearchHint()
  }
}
</script>

<style>
.n-popover.n-popover-shared:has(.search-hint-wrapper) {
  @apply top-4;
}
</style>
<style lang="scss" scoped>
:deep(.search-bar) {
  .n-input__border,
  .n-input__state-border {
    @apply important:border-none;
  }
}
</style>
